/**
 * Two-pathogen co-circulation SEIR with cross-immunity.
 *
 * Standard SEIR assumes single-pathogen dynamics. Real outbreak
 * settings co-circulate (flu × COVID × RSV winter 2022-23 in N
 * hemisphere; dengue serotypes; SARS-CoV-2 sub-variants). Cross-
 * immunity from prior exposure to pathogen A reduces susceptibility
 * to pathogen B by `crossImmunity_AB` (and vice versa).
 *
 * State per individual is a tuple (status_A, status_B) ∈ {S, E, I, R} × {S, E, I, R}.
 * To keep the state-space tractable, we collapse and track only the
 * canonical compartments that matter:
 *   S    — susceptible to both
 *   E_A  — exposed to A, susceptible to B
 *   I_A  — infectious with A, susceptible to B
 *   R_A  — recovered from A, susceptible to B (with cross-protection for B)
 *   E_B  — exposed to B, susceptible to A
 *   I_B  — infectious with B, susceptible to A
 *   R_B  — recovered from B, susceptible to A (with cross-protection for A)
 *   R_AB — recovered from both (no longer susceptible)
 *
 * Cross-immunity: when R_A meets pathogen B at force λ_B, the
 * effective infection rate is λ_B × (1 - crossImmunity_AB). Same for R_B
 * encountering A.
 *
 * Limit: when crossImmunity_AB = crossImmunity_BA = 0, the two
 * pathogens are independent (sum of two SEIRs). When = 1, full cross-
 * protection (R_A immune to B, R_B immune to A).
 */

import { integrateRk4 } from './rk4.js';
import { ENGINE_VERSION } from './sir.js';
import type { EpiResult, BetaScaler } from './types.js';

export interface CoCirculationParams {
  N: number;
  /** Pathogen-A initial conditions. */
  I0_A: number;
  E0_A?: number;
  R0_A?: number;
  /** Pathogen-B initial conditions. */
  I0_B: number;
  E0_B?: number;
  R0_B?: number;
  /** Transmission + recovery rates per pathogen. */
  beta_A: number;
  beta_B: number;
  sigma_A: number;
  sigma_B: number;
  gamma_A: number;
  gamma_B: number;
  /** Cross-immunity reduction in [0, 1]. After A, susceptibility to B × (1 - crossImmunity_AB). */
  crossImmunity_AB: number;
  /** After B, susceptibility to A × (1 - crossImmunity_BA). */
  crossImmunity_BA: number;
  durationDays: number;
  dtDays?: number;
}

export interface CoCirculationOptions {
  betaScaler_A?: BetaScaler;
  betaScaler_B?: BetaScaler;
}

export function simulateCoCirculation(
  params: CoCirculationParams, opts: CoCirculationOptions = {},
): EpiResult {
  const N = params.N;
  if (params.crossImmunity_AB < 0 || params.crossImmunity_AB > 1) throw new Error('crossImmunity_AB ∈ [0,1]');
  if (params.crossImmunity_BA < 0 || params.crossImmunity_BA > 1) throw new Error('crossImmunity_BA ∈ [0,1]');

  // Eight compartments: S, E_A, I_A, R_A, E_B, I_B, R_B, R_AB.
  const I0_A = params.I0_A;
  const E0_A = params.E0_A ?? 0;
  const R0_A = params.R0_A ?? 0;
  const I0_B = params.I0_B;
  const E0_B = params.E0_B ?? 0;
  const R0_B = params.R0_B ?? 0;
  const S0 = N - I0_A - E0_A - R0_A - I0_B - E0_B - R0_B;
  if (S0 < 0) throw new Error('CoCirculation: N < sum of initial');

  const dt = params.dtDays ?? 0.5;
  const t0 = performance.now();

  const f = (t: number, y: number[]): number[] => {
    const S    = y[0]!;
    const E_A  = y[1]!;
    const I_A  = y[2]!;
    const R_A  = y[3]!;
    const E_B  = y[4]!;
    const I_B  = y[5]!;
    const R_B  = y[6]!;
    // R_AB at y[7] doesn't appear on RHS (terminal).

    const scaleA = opts.betaScaler_A ? opts.betaScaler_A(t) : 1;
    const scaleB = opts.betaScaler_B ? opts.betaScaler_B(t) : 1;
    const betaA = params.beta_A * scaleA;
    const betaB = params.beta_B * scaleB;

    // Forces of infection (total I-compartment of each pathogen / N).
    const lamA = (betaA * I_A) / N;
    const lamB = (betaB * I_B) / N;

    // S → E_A at lamA; S → E_B at lamB.
    // R_A → E_B at lamB × (1 - crossImmunity_AB).  (R_A still infectious-to-B but with reduced susceptibility.)
    // R_B → E_A at lamA × (1 - crossImmunity_BA).

    const newE_A_fromS = lamA * S;
    const newE_B_fromS = lamB * S;
    const newE_B_fromR_A = (1 - params.crossImmunity_AB) * lamB * R_A;
    const newE_A_fromR_B = (1 - params.crossImmunity_BA) * lamA * R_B;

    const becomeI_A_fromE_A = params.sigma_A * E_A;
    const becomeI_B_fromE_B = params.sigma_B * E_B;

    const recover_A = params.gamma_A * I_A;
    const recover_B = params.gamma_B * I_B;

    // R_A who acquire B and recover → R_AB. To keep state-space small, we model the
    // R_A → E_B → I_B path as ending in R_AB (after recovery). We don't track
    // E_B-after-A and I_B-after-A as separate compartments — instead, the
    // newE_B_fromR_A flow is collected into a 'shadow' tracking variable, then
    // transitions to R_AB through the same σ_B + γ_B rates.
    //
    // Simplification: lump the post-cross-immunity exposed + infectious into the
    // main E_B + I_B compartments. R_AB grows from γ_B × (I_B fraction that came
    // from R_A). To track this without doubling the state-space, we use a parallel
    // counter: y[8] = pending_R_AB (people who will recover into R_AB).
    //
    // Cleaner alternative: treat R_AB as accumulating γ × (I_B × R_A_fraction_among_pre_E_B).
    // Approximation: at quasi-equilibrium, fraction of I_B coming from R_A is
    // newE_B_fromR_A / (newE_B_fromS + newE_B_fromR_A). Apply this to recover_B.

    const totalNewE_B = newE_B_fromS + newE_B_fromR_A;
    const fracIB_fromR_A = totalNewE_B > 0 ? newE_B_fromR_A / totalNewE_B : 0;
    const totalNewE_A = newE_A_fromS + newE_A_fromR_B;
    const fracIA_fromR_B = totalNewE_A > 0 ? newE_A_fromR_B / totalNewE_A : 0;

    const recover_B_toR_AB = recover_B * fracIB_fromR_A;
    const recover_B_toR_B = recover_B * (1 - fracIB_fromR_A);
    const recover_A_toR_AB = recover_A * fracIA_fromR_B;
    const recover_A_toR_A = recover_A * (1 - fracIA_fromR_B);

    return [
      -newE_A_fromS - newE_B_fromS,                                    // dS
      newE_A_fromS + newE_A_fromR_B - becomeI_A_fromE_A,                // dE_A
      becomeI_A_fromE_A - recover_A,                                    // dI_A
      recover_A_toR_A - newE_B_fromR_A,                                 // dR_A
      newE_B_fromS + newE_B_fromR_A - becomeI_B_fromE_B,                // dE_B
      becomeI_B_fromE_B - recover_B,                                    // dI_B
      recover_B_toR_B - newE_A_fromR_B,                                 // dR_B
      recover_A_toR_AB + recover_B_toR_AB,                              // dR_AB
    ];
  };

  const integrated = integrateRk4(f, [S0, E0_A, I0_A, R0_A, E0_B, I0_B, R0_B, 0], params.durationDays, dt);
  const compartments: Record<string, number[]> = {
    S: [], E_A: [], I_A: [], R_A: [], E_B: [], I_B: [], R_B: [], R_AB: [],
  };
  for (const row of integrated.y) {
    compartments.S!.push(row[0]!);
    compartments.E_A!.push(row[1]!);
    compartments.I_A!.push(row[2]!);
    compartments.R_A!.push(row[3]!);
    compartments.E_B!.push(row[4]!);
    compartments.I_B!.push(row[5]!);
    compartments.R_B!.push(row[6]!);
    compartments.R_AB!.push(row[7]!);
  }

  return {
    t: integrated.t,
    compartments,
    receipt: {
      engineVersion: ENGINE_VERSION,
      seed: 'co-circulation',
      elapsedMs: performance.now() - t0,
      steps: integrated.steps,
    },
  };
}
